import type { ServerMetadata } from "@app/lib/actions/mcp_internal_actions/tool_definition";
import { z } from "zod";

export const COMPANY_KNOWLEDGE_TOOL_NAME = "company_knowledge" as const;

// ONTI-INTEGRATION: agent-facing surface of the onti knowledge graph
// (services/onti). Per ADR docs/onti-dust/05-adr-knowledge-scoping.md the graph
// only ever contains Global-space ("Company Data") content readable by every
// workspace member, so no per-user permission filtering is needed at query
// time — workspace membership (already enforced by the agent loop) is the
// authorization boundary.
export const COMPANY_KNOWLEDGE_TOOLS_METADATA = [
  {
    name: "ask_company_knowledge",
    description:
      "Ask a question against the company's curated knowledge graph and get a " +
      "grounded answer with citations. Answers are drawn from facts extracted " +
      "from the company's shared data (Company Data space); facts a human " +
      "reviewer has rejected or marked stale are excluded, but most facts are " +
      "auto-extracted and not yet individually reviewed. If the graph has no " +
      "matching fact, the tool says so instead of guessing. Prefer this tool " +
      "when the user asks about company-specific facts (people, teams, " +
      "products, policies, decisions), and attribute answers to company " +
      "knowledge rather than presenting them as independently verified.",
    schema: {
      question: z
        .string()
        .min(1)
        .describe("The question to answer from company knowledge."),
    },
    stake: "never_ask",
    // User-facing timeline labels (Korean-market product); tool DESCRIPTIONS
    // stay English — they are read by the model, not the user.
    displayLabels: {
      running: "지식에 질문하는 중",
      done: "지식 질문",
    },
    toolCostCategory: "basic",
    freeUsage: false,
  },
  {
    name: "search_company_knowledge",
    description:
      "Full-text search over the company's curated knowledge facts. Returns " +
      "matching fact statements with their sources; facts a human reviewer " +
      "rejected or marked stale are excluded, but most are auto-extracted and " +
      "not individually reviewed. Use this to look up specific entities or " +
      "topics when you want raw facts rather than a synthesized answer.",
    schema: {
      query: z
        .string()
        .min(1)
        .describe("Search terms (an entity name, topic, or keywords)."),
    },
    stake: "never_ask",
    displayLabels: {
      running: "지식 검색 중",
      done: "지식 검색",
    },
    toolCostCategory: "basic",
    freeUsage: false,
  },
] as const;

export const COMPANY_KNOWLEDGE_SERVER = {
  serverInfo: {
    name: "company_knowledge",
    version: "1.0.0",
    // Builder-facing description — Korean first (searchable as 지식/회사 지식),
    // with the English name kept for findability.
    description:
      "지식 (Knowledge): 회사 지식그래프에서 근거 있는 답변과 사실 검색을 " +
      "제공합니다. 회사 데이터에서 추출한 사실을 사용하며, 검토자가 폐기하거나 " +
      "만료 처리한 사실은 제외됩니다.",
    authorization: null,
    icon: "ActionBrainIcon",
    documentationUrl: null,
  },
  tools: COMPANY_KNOWLEDGE_TOOLS_METADATA,
} as const satisfies ServerMetadata;
